// Story data for The Dark Pan

export const ACTS = [
  { id: 1, title: "The Invitation", span: "Ch. 1–3" },
  { id: 2, title: "The Feeding", span: "Ch. 4–6" },
  { id: 3, title: "The Revolt", span: "Ch. 7–9" },
  { id: 4, title: "The Collapse", span: "Ch. 10–12" },
  { id: 5, title: "The Letting Go", span: "Ch. 13–15" },
];

export const CHAPTERS = [
  {
    id: "the-lullaby",
    number: 1,
    act: 1,
    title: "The Lullaby She Was Told Not To Sing",
    logline: "A Welsh cradle of vowels. A candle that ripples. A boy in a brass bowl.",
    location: "Battersea, London",
    year: "1892, 11:47 PM",
    world: "particle",
    still: "/stills/attic.jpg",
    body: [
      "We open in soot black and white. Rain. Coal smoke. Battersea Power Station half-built, skeletal. Camera low, in mud. A choir of children hums off by seven cents flat.",
      "Inside a cramped attic, Mary Darling, fourteen, clerk's daughter who mends match-factory uniforms, sings her brothers to sleep. Not a nursery rhyme. An old Welsh cradle her mother taught her, made entirely of vowels. A-E-I-O-U-Y. As she sings it in the old tuning, the candle flame ripples. Sand on the windowsill forms a perfect hexagon.",
      "Mrs. Darling slams the window. We do not sing that song in this house. In 1871, twelve children from the Battersea workhouse vanished during choir practice. Official: cholera. Mother to mother: they followed a boy who never aged into abandoned pleasure gardens in the swamp, Neverland, what the mudlarks called it, and never came back. Her own brother was one of the twelve. She still sets a plate.",
      "Mary is logical. She does not believe. Then the cracked vanity is not a mirror for three frames. It is a brass cymatic bowl filled with sand, vibrating. A boy's face in it. Beautiful. Twelve. Eternal. He smiles. This is a window, not a door.",
      "Outside in the rain, Hook watches from the workhouse yard. Thirty-two. Same bone structure as the boy in the bowl. Crude iron hook. He hears Mary singing the old tuning and his tinnitus spikes. He knows another door is about to open. Mrs. Darling slams the door on him: You came back wrong.",
    ],
    coda: "A single blue feather, glowing with Everlit, lands on Michael's chest. It is not a feather. It is a rust flake from a Ferris wheel.",
  },
  {
    id: "the-boy",
    number: 2,
    act: 1,
    title: "The Boy Who Wouldn't Grow",
    logline: "He does not say come to Neverland. He asks if she heard the real song.",
    location: "Nursery / the crossing",
    year: "Midnight",
    world: "particle",
    still: "/stills/split.jpg",
    body: [
      "John wakes. Blue feather floating. Follows it to the window. Pan perched on the sill, shot like a religious painting in a palette too bright for this house. Twelve forever. Patchwork coat. Eyes with no age. Not flying. Held. The air behind him ripples vowel-colors when he sings backward.",
      "You heard the Song, didn't you? The real one. The one they told you not to sing. He sings A-E-I-O-U-Y backward in major, old flat tuning. The boys levitate. Cymatic lift. Bodies less particle, more wave. Singing forward does nothing. Singing backward opens a portal.",
      "Hook breaks in the particle way, soaked, bleeding. Looks exactly like Pan, twenty years older. He's lying by omission. Every rule he bends, I break. I am the first Pan she fell in love with. She spun the wheel backward and sang backward to keep me. The gate that opens with sound sheared my hand. I am trying to rewind what was done.",
      "Pan grabs John and Michael. The nursery wall peels. Behind it is not London. An overgrown abandoned World's Fair, golden hour, impossibly green. A rusted Ferris wheel covered in vines. The brothers step through and go full color. Mary must choose. She steps through to get them. As she crosses, we see her both palettes: coat in color, shadow in noir. Bridge.",
    ],
    coda: "The bowl slams shut. In the workhouse ledger, two new boys appear. Numbers 14 and 15. Breath pulled as blue vapor toward the wheel.",
  },
  {
    id: "neverland-is-real",
    number: 3,
    act: 1,
    title: "Neverland Is Real",
    logline: "Thirty unmapped acres of Battersea swamp. A failed fair. A wheel that is time.",
    location: "Neverland",
    year: "Continuous",
    world: "wave",
    still: "/stills/tink-forest.jpg",
    body: [
      "This place is not imaginary. It is the real Neverland from 1870s maps: unmapped swamp where the 1862 World's Fair failed and was abandoned. The Crown stopped surveying because the mud took it. To police, it did not exist. To lost children, paradise, because no adult came.",
      "Tink runs this place. Not a fairy. High priestess, engineer. Italian Romani, nineteen, barefoot, tuning fork. She tends two things. The Bowl: six feet of brass, black sand, a window that lets her see London and push a boy. The Ferris Wheel: what her father Elias built to rewind time and fetch her mother Liora, who vanished during choir. Wheel forward plus song backward opened endless doors. He never came back.",
      "Mary finds Elias's diary in Romani. Wheel forward + backwards song = endless doors. Wheel backwards + backwards song = rewinds time. Stop wheel + sing forward = riddle solved, closes permanently. Pan admits he turned the wheel forward after she cut his hand. That is why Hook exists: the older dual version. When the wheel rewinds, all damage returns to him.",
      "Tink is torn. Pan is the first boy who did not call her witch when she sang backward. He has her father's broken heart. She thinks if she lets him go he will have that heart. So she traps him, believing she is saving him. Neverland does not work that way.",
    ],
    coda: "In the bowl: endless doors. Different Pans. Different Tinks. All rusted wheels. Sand forms thirteen points, then goes flat.",
  },
  {
    id: "the-choir",
    number: 4,
    act: 2,
    title: "The Choir That Feeds",
    logline: "Every note they sing backward makes another boy heavier.",
    location: "Neverland / Workhouse",
    year: "Dawn",
    world: "wave",
    still: "/stills/split.jpg",
    body: [
      "Morning. The wheel hub is slow. Sand in the bowl settles for the first time in four years. Color at ninety percent. Tootles, six, cuts a knee on rust. Bleeds. Stares at blood like he has never seen it, because in pure wave-state you do not bleed.",
      "Pan conducts. Sing backwards. Like I taught you. As the Lost Boys join, we split the screen and then it becomes the same screen. In the workhouse, blue Everlit vapor is ripped from particle boys' mouths. They collapse. Nosebleeds. Weight doubles. Hook shouts at the sky: He's feeding again.",
      "Mary saw the other film. She confronts him. Pan is exhausted, addict-logic. I extended us. The bowl needs Everlit. Everlit is breath. If it stops we all collapse. I am trying to find the key to rewind without tax. Mary: There is no key. There is only the song.",
      "Tink wakes and sings six tones backward for two seconds. Color drains twenty percent. Pan ages two years in front of us, then snaps back. When she sings backward while the wheel moves backward, time stutters. Tootles coughs up blood that is ten years old.",
    ],
    coda: "Pan whispers the riddle. Stop wheel + sing forwards = solved. But if I stop the wheel and sing forwards, I am trapped alone forever.",
  },
  {
    id: "hook-crosses",
    number: 5,
    act: 2,
    title: "Hook Crosses",
    logline: "He sings backward alone, broken voice, and the bowl hears the other side.",
    location: "Workhouse → Neverland",
    year: "The first crossing",
    world: "merged",
    still: "/stills/hook.jpg",
    body: [
      "Hook has had enough. He takes the original tuning fork Tink used in 1888, the one that took his hand, and strikes it against iron. He sings backward alone. For the first time the bowl hears a backward tone from the other side. A mud puddle in Neverland becomes factory floor. He steps through.",
      "Color does not take fully. He remains desaturated, a Sin City character walked into a Disney film. Where his feet touch, the too-green grass dies. Particle weight entering wave. Lost Boys scream. They have been told Hook is the villain. Pan sees his own older face and freezes.",
      "Hook does not fight. He falls to his knees. Looks at Tink, first love, now nineteen, the age she was when she lost him. Ti ho sentita cantare al contrario. Hai provato. I heard you sing backward. You tried. When she touches his face they flicker, trying to become one person. For three seconds: Peter Davies, seventeen, whole, no hook, no eternal youth. Then they split again.",
      "Hook makes Pan fly ten feet. Hook's leg snaps, audible, in front of the children. No blood. Causality tax. Silence. Two timelines fabricated to tell one story become one timeline in the children's eyes. Pan finally breaks. I didn't know. She told me you were a pirate.",
    ],
    coda: "Tootles sings Y forwards, the first forward tone ever sung in Neverland with intent to close. He ages six months in two seconds. In the workhouse, Number 3 can breathe.",
  },
  {
    id: "the-mother",
    number: 6,
    act: 2,
    title: "The Mother Who Warns",
    logline: "The full lullaby was always backwards to open, forwards to close when the wheel is stopped.",
    location: "World A and B",
    year: "Origin of the warning",
    world: "merged",
    still: "/stills/mrs-darling.jpg",
    body: [
      "Mrs. Darling was thirteen in 1871, sick the day the first choir vanished. She watched twelve friends follow a boy with a pipe into the swamp, singing backward. Adults taught the song forwards to keep children safe, because forwards does nothing. She remained. Married. Had Mary, John, Michael. Never stopped setting thirteen plates.",
      "She knew Tink as a young Italian girl asking about the old lullaby, trying to bring a mother back. Mrs. Darling told her not to sing backward. Tink sang anyway. Now the mother who warns follows blue vapor through rain to the edge of the swamp and steps through for the first time in twenty years. Black and white, a desaturated mother in a Disney world.",
      "She teaches Mary the meanings. Y: I yield. U: you return. O: we are one. I: I remember. E: we end. A: amen, so be it. Backward spells portal. Forward spells close, but only when the wheel is stopped. As she sings forwards and Hook jams the rusted gear with his hook, the giant bowl stops. Sand settles into a thirteen-point star. Color drains to honest dusk.",
      "Tink panics. She spins the wheel forward and sings backward loud. Endless doors. For three frames we see Elias in one door, Liora in another. Hook and Mrs. Darling are thrown back through the puddle. Mary is left holding the knowledge: forward does nothing alone. Forward while the wheel is stopped closes permanently.",
    ],
    coda: "Neverland is bright again, but the sky has hairline cracks where noir shows through.",
  },
  {
    id: "battery-farm",
    number: 7,
    act: 3,
    title: "The Battery Farm",
    logline: "A twelve-year-old cult leader who knows he is dying if the choir stops.",
    location: "Neverland, overdrive",
    year: "Two hours after Act II",
    world: "wave",
    still: "/stills/neverland.jpg",
    body: [
      "After Tink slammed the wheel forward plus backward, the resonator is in overdrive. Sand is a solid disc. Hum is a whine. Color at one hundred twenty percent, sickly. Leaves neon. Sky cyanotic. Lost Boys sunburnt from too much light. In the factory, quota doubles. Hands bleed.",
      "Pan forces choir rows. A! E! I! O! U! Y! BACKWARDS! Again! Mary stands in the middle, half-color half-noir coat, the only one not singing. She sabotages. Teaches Tootles, who retained his age, to hum forwards under the backward choir. Wave interference. A Lost Boy next to him feels weight and stumbles.",
      "Pan hears the dissonance and slaps Tootles. First violence Neverland has ever allowed. Tootles is seven now, and the cry is real. Tink: He is not a battery. Pan: We all are. She made us batteries. Then the reveal: Pan is the seventh boy to wear the title. Tink has been moving the wave-self boy to boy for four years to keep the idea of Peter alive, because she could not bear her father's broken heart.",
    ],
    coda: "Fifteen boys singing backward at one hundred twenty percent color. Fifteen counterparts bleeding noses in perfect sync.",
  },
  {
    id: "counterparts",
    number: 8,
    act: 3,
    title: "The Counterparts Revolt",
    logline: "If we stop singing their song with our breath, their bowl stops.",
    location: "Battersea workhouse",
    year: "Simultaneous",
    world: "particle",
    still: "/stills/workhouse.jpg",
    body: [
      "Stay in World B. Hook wakes, leg re-broken. He draws the cymatic pattern on the factory wall in chalk. That pretty place they tell you about is real. But you are working there too. Every match you make here is a laugh there. He teaches them to hum Y-U-O-I-E-A forwards in the chest, like a prayer. Particle resistance. Become un-battery-able.",
      "As fifteen particle boys hum forwards together, low, rumbling, something breaks Neverland. Voices crack. Everlit thins. The wheel creaks toward stopped. Color drops from sickly neon to honest golden hour. Pan feels weight in his legs for the first time and stumbles.",
      "Mrs. Darling joins the humming. Adult breath is denser. The door crack widens. She and Hook step through, still noir in a color world, bringing real rain with them. Cold grey London rain hits Everlit lanterns and they sizzle. Lost Boys look up, confused, feeling cold for the first time. Pan tries to sing backward alone. A single voice cannot hold the bowl when the wheel is stopped. He ages twelve to fifteen. Voice cracks. Hands bigger.",
    ],
    coda: "Pan sits down heavy, crying, not Pan but Peter, the boy under the title.",
  },
  {
    id: "six-tones",
    number: 9,
    act: 3,
    title: "The Six Tones",
    logline: "Thirty boys facing themselves across a bowl. Same eyes. Different clothes.",
    location: "The resonator clearing",
    year: "Twilight, fifty / fifty",
    world: "merged",
    still: "/stills/bowl.jpg",
    body: [
      "All characters occupy the same physical space. Doors open both ways. Wide shot: half Disney, half Sin City, rain in both. Fifteen Lost Boys and fifteen counterparts stand facing each other across the bowl. Same faces. Different ages. Same eyes.",
      "Tink confesses, not as exposition, as grief. When my father died he left a book. Thirteen original tones, not twelve. The church kept twelve to make the choir sound perfect, but perfect is a trap. Twelve spins forever. Thirteen resolves. Backwards they open. Nothing forward. Wheel forward plus backward: endless doors, where dad went looking for mum. Wheel backward plus backward: rewinds time. Stop wheel and sing forwards: riddle solved. Closes permanently.",
      "Pan, now seventeen, is scared. If we sing forwards while the wheel is stopped I stay trapped alone forever. Hook: Yes. World returns and the kids stop dying. That is the price. Except the hand. You keep that. You go back to that moment and stay. Pan looks at the Lost Boys. Sees tired. Sees his own hands, now a man's. He agrees.",
    ],
    coda: "Thirty-four voices inhale together. Mary starts. Y, forwards, in minor. Holds.",
  },
  {
    id: "yield",
    number: 10,
    act: 4,
    title: "Y-U-O, I Yield, You Return, We Are One",
    logline: "Three tones. Color leaving Neverland and arriving as weather in the factory.",
    location: "Worlds merged",
    year: "The ritual",
    world: "merged",
    still: "/stills/cymatic-top.jpg",
    body: [
      "Mary holds Y forwards. Voice cracks because minor is harder. Yellow drains from Pan's ribbons, from the field flowers, from the lanterns, and floods the workhouse brick like a first sunrise. Tink translates: Y is I yield. You must yield the idea that you are special. Pan yields. Sings. Ages nineteen. Coat becomes a work jacket.",
      "U. Tootles leads, first to age. Blue drains the lagoon. Impossible turquoise becomes muddy swamp water. Blue vapor streams to the factory, where the sky above black smoke shows first blue in twenty years. U is you return. You must return what you stole: breath. Memories flood both ways. Lost Boys remember factory, beating, matches. Particle boys remember flying five feet. John and John-P touch hands across the bowl rim. A small spark of thirteen-point geometry.",
      "O. Mrs. Darling's alto, hardest. O is we are one. You must accept you are not two films. Red drains Pan's tunic and Hook's coat and Tink's lips. In the factory, blood returns to burned fingers, and for the first time the particle boys' hands are warm. Pan and Hook, singing the same note, now the same age, twenty-six. Double exposure becoming single exposure. First pair merges: Tootles, one boy, seven, crying real tears, smiling.",
    ],
    coda: "Three tones complete. Color now thirty percent Neverland, seventy percent factory. Tink inhales for I.",
  },
  {
    id: "remember",
    number: 11,
    act: 4,
    title: "I-E, I Remember, We End",
    logline: "She pulled a dead boy through a pocket and called it love.",
    location: "Bowl at twenty percent",
    year: "Honest light",
    world: "merged",
    still: "/stills/tink.jpg",
    body: [
      "I. Tink leads because this is confession. I is I remember. You must remember why you opened. As she sings forwards we get the honest memory, not noir, not Disney. 1888. Tink fifteen, in love with Peter Davies, fifteen, mudlark who worked the press. He dies. Not romantic. Industrial accident. Crushed. She finds her father's book. Thirteen tones, not twelve. She goes to the abandoned fairground at midnight and sings backward. She pulls Peter through as wave, fifteen, alive, translucent. She thinks she saved him. She does not see the particle counterpart still dead at the press, now entangled, losing.",
      "She could not sing I forwards while the wheel was stopped, because I remember means he died, and it means staying alone forever in the trap. So she brought a new boy. Then another. Pan number two, number three, number seven. Moving the wave-self to keep the idea of Peter alive.",
      "E. The merged man leads. E is we end. You must end the story you told yourself about being special. Adult baritone, both voices now one. Lost Boys sit down heavy. Age catching up. Honest children. White drains, the last Disney white, and floods the workhouse as real overcast London daylight. The hum that held the pocket four years is almost silent. Rain. Breathing. Wind. One tone left.",
    ],
    coda: "Mary steps forward. A must be sung by an innocent who chooses to grow up willingly, not forced. She inhales.",
  },
  {
    id: "amen",
    number: 12,
    act: 4,
    title: "A, Amen",
    logline: "Not an explosion. Consonance. The riddle solved.",
    location: "The thirteenth state",
    year: "Wheel stopped",
    world: "honest",
    still: "/stills/ending.jpg",
    body: [
      "Mary sings A, forwards, in minor, seven cents down. A is amen. So be it. Sixth tone: letting go. Collapse is not explosion. It is consonance. Clear vapor, not blue, returns to air. The bowl stops. Zero percent spin. Hook's hook remains jammed in the gear. Sand settles into a perfect thirteen-point star, then goes slowly flat. Silence. The thirteenth tone is not a tone. It is the silence after the chord.",
      "Ten seconds no one breathes. No score. No hum. Just rain. Then: fifteen boys, one each, clothes half patchwork half uniform. Pan and Hook now one man, thirty-two, Peter Davies Hook, whole, hook still on the hand because the loss is real and stays. Tink nineteen, honest light, no glow, hair wet, holding a brass bowl that is just a bowl. Mrs. Darling holds Mary, John, Michael. One each.",
      "Neverland is gone. We are in Battersea swamp. Abandoned World's Fair. Rusted Ferris wheel stopped overgrown at dusk. No magic. The place vagrants called Neverland because it was never on a map, just swamp again. Peter Davies Hook: I remember dying. You didn't save me. You saved the idea of me. Thank you for letting me go. Tink: Ti lascio andare. I let you go.",
      "He walks back through the closing portal. Reverse of the original shearing: Pan goes back through to Neverland alone forever. Hook stays in the real world, hand lost willingly this time. Wide shot: a boy of twelve sitting on a stopped Ferris wheel. No Lost Boys. No color. Just rust. Door closed permanently. Trapped forever. World returns. Kids stop dying.",
    ],
    coda: "Mary could sing backward again. She knows how now. She looks at fifteen tired honest boys and her brothers, one each. She chooses not to. She throws the bowl into the swamp. It sinks.",
  },
  {
    id: "funeral",
    number: 13,
    act: 5,
    title: "The Funeral For Two Boys Who Were One",
    logline: "They bury not a body but a hook. Anchor, never weapon.",
    location: "Battersea swamp",
    year: "Dusk, zero percent magic",
    world: "honest",
    still: "/stills/pan-alone.jpg",
    body: [
      "Rain has stopped. Real London dusk, the blue-grey hour after rain when brick looks warm. They bury the crude iron hook in the swamp where the portal was, because mudlarks buried their own in unmapped ground. The Crown would not. Tink tries to lift the Ferris wheel. She cannot. It is stopped permanently.",
      "John, now one John who remembers flying five feet and remembers match-factory burns, asks: Is he Pan or Hook? Mary: He was Peter. Before the title. Now he is both. One staying alone forever so we can leave. The hook was never a weapon. When the first Pan tried to leave, the sound-gate sheared. Tink gave him the hook to anchor him particle-side so he could not fly back and die as wave.",
      "We intercut. Pan, twelve, sitting on the stopped wheel alone. He sings forwards. Nothing happens. Riddle solved. The bowl, somewhere, still lets him see London. He cannot affect time without the wheel. He is alone forever. Back in the swamp Tink sings the lullaby in major, honest tuning, not flat. No cymatic effect. Vowels are just vowels. A faint thirteen-point star in a puddle from rain ripples, then flat. That is how we know the pocket closed.",
    ],
    coda: "Mrs. Darling says the names of twelve Peters in Welsh. Sets plates in the mud. Tink sings A over the grave. So be it. No light. Just breath.",
  },
  {
    id: "both",
    number: 14,
    act: 5,
    title: "The Boys Who Remember Both",
    logline: "We fabricated two timelines to tell one story. Now we are one world.",
    location: "Darling house / workhouse",
    year: "Night",
    world: "honest",
    still: "/stills/attic.jpg",
    body: [
      "They walk from swamp to Battersea without a door, because the door closed permanently. Streets no longer noir, no longer Disney. 1892 London, gas lamps, real. Fifteen boys in strange clothes, half fairground rags, half factory uniforms. The foreman, who was a villain in noir, is just a tired manager of fifty. He tears the numbered ledger and throws it on the fire. First time fire gives honest warmth, not Everlit blue.",
      "They sleep on the attic floor because the attic has a window, and the window still has sand on the sill, and the sand is flat. John sits with both memories. I flew five feet. I burned a thumb making a match. Both true. Mary teaches them a new song that is a round: one group backward in major, one group forwards in minor, at the same time, seven cents flat, while the wheel is stopped. Dissonance resolving into silence. The thirteenth tone. Not magic. Harmony that says: we can play and we can work. We choose when.",
      "Tink arrives with the bowl, dug up, not to use. She gives it to Mrs. Darling. Your mother warned. You were right. Bury it where no child sings backward. Mrs. Darling takes it to the Thames and drops it. Sinks. No light. Last glimpse in the water: Pan alone forever on the stopped wheel, watching. Tink asks to stay. Not as fairy. Lodger. Italian girl who knows thirteen tones. Mrs. Darling says yes.",
    ],
    coda: "Mary writes the first diary entry of the film. He stayed alone forever so we could leave.",
  },
  {
    id: "stillness",
    number: 15,
    act: 5,
    title: "Stillness, Chosen",
    logline: "There were thirteen tones. We kept twelve to spin forever. The thirteenth is silence.",
    location: "One world",
    year: "1893, six months later",
    world: "honest",
    still: "/stills/ending.jpg",
    body: [
      "Epilogue. No split-screen. No color tricks. One film stock, desaturated, like late afternoon light that is actually real. Fifteen boys apprenticed not to the factory but to trades: carpenter, baker, mudlark who now maps the swamp honestly so no child gets lost because a place was unmapped. They still remember both. They play the swamp after work. They come home. Not Lost. Found.",
      "John, nine, writes a story about a boy who flew, then landed, then stayed alone forever so others could go home. Michael sings forwards at school because forwards does nothing, and that is safe. Mary, fifteen, clerk, keeps a diary of thirteen tones as music theory, not as spell, and puts it in a drawer. Tink teaches children to sing honest tuning, not flat. She visits the hook-grave once a week, places the tuning fork, says so be it, and leaves. She has let go of love.",
      "Mary walks to the swamp at dawn. No door. Just a puddle where a bowl sank. She sings the six tones backward, very quietly, not to open. The pocket is closed. Backward does nothing now. Only memory. She finishes on A, amen, forwards, while the wheel is stopped, and stops. Silence. The thirteenth tone. Stillness chosen.",
    ],
    coda: "Wide shot. Left: rusted wheel, tiny figure on top, twelve forever, looking at a London he can see and cannot reach. Right: factory chimneys, honest smoke. Middle: fifteen boys walking to work, one group, no sides. The wheel never moves again.",
  },
];

export const SONGS = [
  {
    id: "trailer",
    title: "The Dark Pan Trailer",
    duration: "2:15",
    youtubeId: "GFvxleKnwJY",
    thumbnail: "/stills/battersea.jpg",
    category: "trailer",
  },
  {
    id: "chapter1",
    title: "Chapter 1",
    duration: "8:42",
    youtubeId: "Z2Nnm8xa5IA",
    thumbnail: "/stills/attic.jpg",
    category: "story",
  },
  {
    id: "theme",
    title: "Some Songs Don't Take You to Neverland",
    duration: "4:32",
    youtubeId: "HWpa8BzECl0",
    thumbnail: "/stills/theme-thumb.jpg",
    category: "music",
    lyrics: `Second star to the right, straight on 'til morning
They told us that's where the magic was
But maps lie, and so do lullabies
When the people who draw them need something from us

Some songs don't take you to Neverland
They take you to a factory floor
Where your breath becomes someone else's light
And your dreams are a quota and nothing more`,
  },
  {
    id: "abc",
    title: "tink tinker Pan (ABC Song)",
    duration: "3:15",
    youtubeId: "cse3qufxz2g",
    thumbnail: "/stills/abc-thumb.jpg",
    category: "music",
    lyrics: `A is for Always, the promise they break
B is for Battersea, the breath that they take
C is for Choir, the children who sing
D is for Door that the backward notes bring`,
  },
];

export const CHARACTERS = [
  {
    id: "mary",
    name: "Mary Darling",
    role: "The bridge",
    age: "14",
    still: "/stills/mary.jpg",
    bio: [
      "Clerk's daughter. Mends match-factory uniforms. First person who will see color in both worlds: coat in color, shadow in noir.",
      "She is the one who can carry someone through. She is the one who must sing A — amen — because A has to be sung by someone who chooses to grow up willingly.",
      "Not Disney Wendy. She does not clap. She throws the bowl in the swamp and walks her brothers home through mud that is just mud.",
    ],
  },
  {
    id: "pan",
    name: "Pan (No. 7)",
    role: "The wave",
    age: "12, seven times",
    still: "/stills/pan-alone.jpg",
    bio: [
      "Patchwork coat, too-bright ribbons, eyes with no age. Not flying. Held. The seventh boy to wear the title. A mudlark who thought eternal life was escape from the factory.",
      "He did not know the choir was a battery. He was trying to solve a riddle his father-figure never finished: how to rewind without tax. Addict logic. Chosen eternal.",
      "The ending he agrees to: go back to the moment the hand was cut, stay in Neverland alone forever, door closed permanently. World returns. Kids stop dying.",
    ],
  },
  {
    id: "hook",
    name: "Hook / Peter Davies",
    role: "The particle",
    age: "32",
    still: "/stills/hook.jpg",
    bio: [
      "First Pan Tink fell in love with. Not a pirate. Not a villain. Police of the other side. Crude iron hook, not ornate. The hook was an anchor so he could not fly back and die as wave.",
      "He protects twenty orphan boys in the workhouse. Every time Pan sings backward, Hook takes the fall — until the over-rev distributes the tax to all of them.",
      "Same face as Pan, twenty years of suffering apart. He will do anything to stop his counter-self from using children. He does not, at first, know using children keeps both of them alive.",
    ],
  },
  {
    id: "tink",
    name: "Tink",
    role: "The tinkerer",
    age: "19",
    still: "/stills/tink.jpg",
    gallery: ["/stills/tink.jpg", "/stills/tink-forest.jpg", "/stills/tink-window.jpg", "/stills/tink-cliff.jpg"],
    bio: [
      "Italian Romani. High priestess, engineer, not a fairy. Barefoot. Tuning fork. Only one who can live in both worlds because she knows the Song backward, taught down a line of mothers.",
      "TINK is a verb. She tinked — past tense — built the bowl. She saw her father's broken heart trying to fetch her mother Liora through endless doors, and she swore Peter would not have that heart. So she trapped him, thinking she was saving him.",
      "She could sing backward all day to open. She could never sing A forwards while the wheel was stopped and mean it. So be it. Stay alone forever. Ti lascio andare.",
    ],
  },
  {
    id: "mrs",
    name: "Mrs. Darling",
    role: "The mother who warns",
    age: "38",
    still: "/stills/mrs-darling.jpg",
    bio: [
      "Was thirteen in 1871, sick the day the first choir vanished. Watched twelve friends follow a boy with a pipe into the swamp. Her brother was one of them. She still sets a plate.",
      "She knows the song because her mother taught her to CLOSE, not open. Church removed the backward because it wanted children to stay, obedient. Sacred geometry: thirteen original tones, twelve kept for control, the thirteenth hidden.",
      "She is the adult who knows how to handle loss. She says the names. She takes the bowl to the Thames and drops it. No light. No ripple pattern.",
    ],
  },
  {
    id: "elias",
    name: "Elias Tinkertoff",
    role: "The father who built time",
    age: "Gone",
    still: "/stills/neverland.jpg",
    bio: [
      "Gypsy engineer, long line. His mother and grandmother carried the Song. He built the Song into a Ferris wheel because his wife Liora vanished during choir, singing backward to close a pocket. The door took her.",
      "Wheel forward plus song backward opened endless dimensions looking for her. He never returned. The wheel sat and rusted. Pan discovered it. Nobody knew what it did.",
      "His diary is the riddle the whole film is solving. Stop the wheel. Sing forward. Close permanently.",
    ],
  },
];

export function getChapter(id) {
  return CHAPTERS.find((c) => c.id === id);
}

export function getAdjacent(id) {
  const i = CHAPTERS.findIndex((c) => c.id === id);
  return {
    prev: i > 0 ? CHAPTERS[i - 1] : undefined,
    next: i >= 0 && i < CHAPTERS.length - 1 ? CHAPTERS[i + 1] : undefined,
  };
}

export function getSong(id) {
  return SONGS.find((s) => s.id === id);
}

export function getCharacter(id) {
  return CHARACTERS.find((c) => c.id === id);
}
